import React, { useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { loadEstateOwnedFilesRoutine, loadEstateFoldersRoutine } from 'ducks/estateDocuments/actions'
import { getApiErrors } from 'utils/errors'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const DANGER = '#b00020'

const EstateFileDeleteModal = ({ estateId, file, folderId, onClose }) => {
  const dispatch = useDispatch()
  const [deleting, setDeleting] = useState(false)
  const displayName = file.displayName || file.display_name || file.name || 'this document'

  const handleDelete = async () => {
    if (deleting) return
    setDeleting(true)
    try {
      await estateDocumentsService.deleteEstateFile({ estateId, fileId: file.id })
      toast.success(`"${displayName}" deleted.`)
      dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId, page: 1, reloadLoaded: true }))
      dispatch(loadEstateFoldersRoutine({ estateId }))
      onClose()
    } catch (error) {
      const apiError = getApiErrors(error)
      if (apiError?.trim()) toast.error(apiError)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Dialog open onClose={deleting ? undefined : onClose} maxWidth='xs' fullWidth>
      <Container>
        <Title>Delete "{displayName}"?</Title>
        <Body>
          This document will be removed from the estate's documents and can no longer be viewed or downloaded. This cannot be undone here.
        </Body>
        <Footer>
          <CancelButton type='button' onClick={onClose} disabled={deleting}>
            Cancel
          </CancelButton>
          <DeleteButton type='button' onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete document'}
          </DeleteButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateFileDeleteModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Body = styled.p`
  margin: 0;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${ink.soft};
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover:not(:disabled) {
    color: ${ink.base};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const DeleteButton = styled.button`
  min-height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: ${radius.sm};
  background: ${DANGER};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 160ms ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
